import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import { useCallback, useEffect, useMemo } from 'react';
import { type FileWithPath } from 'react-dropzone';
import { useAnalytics } from '@/services/analytics';

import { type Asset } from '@/services/db/asset/entity';

import { fileSize } from '../utils';
import { useAssetOptimizerModal, usePromptOptimize } from '../optimizer';
import { OPTIMIZATION_THRESHOLD_SIZE } from '../utils';
import { AssetUploadPopup } from './asset-upload-popup';
import { ConfirmAbortUploadModal, useConfirmAbortUploadModal } from './confirm-abort-upload-modal';
import { useAssetUpload } from './use-asset-upload';
import { type AssetProcessedFile, isImageAsset, type AllowedFileCategory, type AssetUploadState } from './utils';

const $uploadModalOpen = atom(false);

export const $assetUploadState = atom<AssetUploadState>({
  pendingFiles: [],
  acceptedFiles: [],
  rejectedFiles: [],
  parentFolderId: null,
  uploadedAssets: [],
  parentFolderName: '',
  onUpload: undefined,
});

export function useUploadAsset() {
  const isOpen = useStore($uploadModalOpen);

  const uploadFiles = useCallback(
    (
      files: FileWithPath[],
      {
        allowedFileCategories,
        parentFolderId,
        parentFolderName,
        onSuccessfulUpload,
      }: {
        allowedFileCategories?: AllowedFileCategory[];
        parentFolderId?: string | null;
        parentFolderName?: string;
        onSuccessfulUpload?: () => void;
      },
    ) => {
      return new Promise<Asset[]>((resolve) => {
        const onUpload = async (result: Asset[]) => {
          onSuccessfulUpload?.();
          resolve(result);
          return;
        };

        $assetUploadState.set({
          allowedFileCategories,
          pendingFiles: files,
          acceptedFiles: [],
          rejectedFiles: [],
          parentFolderId: parentFolderId ?? null,
          uploadedAssets: [],
          parentFolderName: parentFolderName ?? '',
          onUpload,
        });
        $uploadModalOpen.set(true);
      });
    },
    [],
  );

  const onClose = useCallback(() => {
    $uploadModalOpen.set(false);
  }, []);

  return [useStore($assetUploadState), { isOpen, uploadFiles, onClose }] as const;
}

export function UploadAssetModal() {
  const { track } = useAnalytics();
  const [, promptOptimize] = usePromptOptimize();
  const { show: showOptimizer } = useAssetOptimizerModal();
  const upload = useAssetUpload();
  const [{ allowedFileCategories, pendingFiles, acceptedFiles, rejectedFiles, uploadedAssets }, { isOpen, onClose }] =
    useUploadAsset();
  const confirmAbortUploadModal = useConfirmAbortUploadModal();

  const allUploadsComplete = useMemo(
    () =>
      (acceptedFiles?.length ?? 0) > 0 &&
      acceptedFiles?.every((file) => file.state === 'SUCCESS' || file.state === 'FAILED'),
    [acceptedFiles],
  );

  const hideProgressBar = allUploadsComplete;

  useEffect(
    function triggerUploadOnOpen() {
      if (!isOpen || pendingFiles == null || pendingFiles.length === 0) return;

      void upload({
        files: pendingFiles,
        allowedFileCategories,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only trigger on open/files change
    [isOpen, pendingFiles],
  );

  useEffect(
    function closePopupWhenComplete() {
      if (!isOpen || allUploadsComplete !== true) return;

      const hasNoFailedUploads = acceptedFiles?.some((file) => file.state === 'FAILED') !== true;
      const hasNoRejectedFiles = (rejectedFiles?.length ?? 0) === 0;

      if (confirmAbortUploadModal.isOpen) {
        void confirmAbortUploadModal.close();
      }

      if (hasNoFailedUploads && hasNoRejectedFiles) {
        if (acceptedFiles != null && acceptedFiles.length === 1) {
          const asset = uploadedAssets?.[0];

          if (asset != null && isImageAsset(asset) && asset.size >= OPTIMIZATION_THRESHOLD_SIZE) {
            void promptOptimize({
              onConfirm: () => {
                showOptimizer({ originalAsset: asset });
                track('assetOptimizer', 'optimizeImage', {
                  'Builder Type': 'design-studio',
                  original_size: fileSize(asset.size),
                });
              },
            });

            void onClose();
            return;
          }
        }

        const timeoutId = setTimeout(() => {
          void onClose();
        }, 2000);

        return () => clearTimeout(timeoutId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close logic depends on upload completion, not all deps
    [
      isOpen,
      acceptedFiles,
      rejectedFiles,
      uploadedAssets,
      onClose,
      showOptimizer,
      promptOptimize,
      allUploadsComplete,
      confirmAbortUploadModal.isOpen,
    ],
  );

  const handleOptimizeOversizedFile = useCallback(
    (file: AssetProcessedFile) => {
      if (file._file == null) return;

      const objectUrl = URL.createObjectURL(file._file);

      const syntheticAsset = {
        id: String(Date.now()),
        name: file.name,
        path: objectUrl,
        size: file.size,
        parent_id: $assetUploadState.get().parentFolderId ?? null,
        is_dir: false,
        in_use: false,
        created_at: new Date(),
        created_by: { id: '', email: '', first_name: '', last_name: '' },
        updated_at: new Date(),
        updated_by: { id: '', email: '', first_name: '', last_name: '' },
      } as Asset;

      onClose();
      showOptimizer({
        originalAsset: syntheticAsset,
        onCleanup: () => URL.revokeObjectURL(objectUrl),
      });
    },
    [onClose, showOptimizer],
  );

  if (!isOpen) return null;

  const allFiles = [...(acceptedFiles ?? []), ...(rejectedFiles ?? [])];

  return (
    <>
      <AssetUploadPopup
        files={allFiles}
        hideProgressBar={hideProgressBar}
        onClose={onClose}
        onOptimize={handleOptimizeOversizedFile}
      />
      <ConfirmAbortUploadModal />
    </>
  );
}
